import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/shared/lib/utils/classnames';

const buttonVariants = cva(
  'label-small-b inline-flex items-center justify-center gap-[6px] whitespace-nowrap rounded-[4px] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        primary:
          'bg-[#0084ff] text-white hover:bg-[#0073e6] disabled:bg-[#bfdfff] disabled:text-white disabled:cursor-not-allowed',
        light:
          'bg-[#e8ebed] text-black hover:bg-[#d9dde0] disabled:bg-[#e8ebed] disabled:text-[#a6a9ab] disabled:cursor-not-allowed',
        white:
          'bg-white border border-[#d9dde0] text-black hover:bg-[#f5f5f5] disabled:bg-white disabled:border-[#d9dde0] disabled:text-[#a6a9ab] disabled:cursor-not-allowed',
        black:
          'bg-black text-white hover:bg-[#1a1a1a] disabled:bg-[#252627] disabled:text-[#67696a] disabled:cursor-not-allowed',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 text-sm',
        sm: 'h-[40px] px-4 py-[18px]',
        md: 'h-[48px] px-4 py-[18px]',
        lg: 'h-[56px] px-4 py-[18px]',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'variant' | 'size'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** 왼쪽 아이콘 (ReactNode) */
  iconLeft?: React.ReactNode;
  /** 오른쪽 아이콘 (ReactNode) */
  iconRight?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      iconLeft,
      iconRight,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // 아이콘 크기 결정 (size에 따라)
    const iconSizeClass =
      size === 'sm' ? 'w-4 h-4' : size === 'md' || size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';

    // 아이콘을 ReactNode로 받아서 크기 조정
    const renderIcon = (icon: React.ReactNode) => {
      if (!icon) return null;
      if (React.isValidElement(icon)) {
        const iconElement = icon as React.ReactElement<{ className?: string }>;
        return React.cloneElement(iconElement, {
          className: cn(iconSizeClass, iconElement.props?.className),
        });
      }
      return <span className={iconSizeClass}>{icon}</span>;
    };

    // asChild 모드에서는 Slot이 단일 children만 받으므로 iconLeft/iconRight 무시
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          disabled={disabled}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {iconLeft && (
          <span className="flex items-center justify-center">{renderIcon(iconLeft)}</span>
        )}
        {children}
        {iconRight && (
          <span className="flex items-center justify-center">{renderIcon(iconRight)}</span>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
